import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { NovedadApiDto } from '../contracts/backend-api.contracts';
import { NOVEDADES_MOCK } from '../mocks/novedades.mock';
import { Novedad, RolPlataforma } from '../models/novedades.models';
import { Postulacion } from '../models/postulaciones.models';
import { environment } from '../../../environments/environment';
import { InvitacionPostulacion, RolInvitacion } from '../models/invitaciones.models';
import { InvitacionesService } from './invitaciones.service';
import { PatrocinioService } from './patrocinio.service';
import { PostulacionAccionLog, PostulacionesLogService } from './postulaciones-log.service';
import { PostulacionesService } from './postulaciones.service';
import { roleHasNotifications } from '../utils/roles.util';

@Injectable({
  providedIn: 'root',
})
export class NovedadesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/novedades`;
  private readonly leidasIds = new Set<string>();
  private readonly notificacionesState$ = new BehaviorSubject<Novedad[]>([]);
  private readonly conteoNoLeidasSubject = new BehaviorSubject<number>(0);

  readonly notificaciones$ = this.notificacionesState$.asObservable();
  readonly conteoNoLeidas$ = this.conteoNoLeidasSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly invitacionesService: InvitacionesService,
    private readonly patrocinioService: PatrocinioService,
    private readonly postulacionesService: PostulacionesService,
    private readonly postulacionesLogService: PostulacionesLogService
  ) {}

  marcarComoLeida(id: string): void {
    this.leidasIds.add(id);
    const actual = this.notificacionesState$.value;
    const actualizados = actual.map((item) => (item.id === id ? { ...item, leida: true } : item));
    this.notificacionesState$.next(actualizados);
    this.recalcularConteo(actualizados);
  }

  marcarTodasComoLeidas(): void {
    const actual = this.notificacionesState$.value;
    actual.forEach((item) => this.leidasIds.add(item.id));
    const actualizados = actual.map((item) => ({ ...item, leida: true }));
    this.notificacionesState$.next(actualizados);
    this.conteoNoLeidasSubject.next(0);
  }

  private recalcularConteo(items: Novedad[]): void {
    const noLeidas = items.filter((item) => !item.leida).length;
    this.conteoNoLeidasSubject.next(noLeidas);
  }

  private aplicarEstadoLectura(items: Novedad[]): Novedad[] {
    return items.map((item) => (this.leidasIds.has(item.id) ? { ...item, leida: true } : item));
  }

  getNovedadesPorRol(rol: RolPlataforma): Observable<Novedad[]> {
    if (!roleHasNotifications(rol)) {
      return of([]);
    }

    const baseNovedades$ = this.getBaseNovedadesPorRol(rol);

    let novedadesObservable$: Observable<Novedad[]>;

    if (rol === 'postulante') {
      novedadesObservable$ = combineLatest([baseNovedades$, this.postulacionesService.getMisPostulaciones(), this.postulacionesLogService.logs$]).pipe(
        switchMap(([novedadesBase, postulaciones, logs]) => {
          const postulacionesAjustadas = this.patrocinioService.aplicarEstadoPatrocinio(postulaciones);
          const idsPostulacion = postulacionesAjustadas
            .filter((item) => item.rolesPermitidos.includes('postulante'))
            .map((item) => item.idPostulacion);
          const novedadesCambios = this.buildCambioNovedadesProceso('postulante', postulacionesAjustadas, logs);

          if (idsPostulacion.length === 0) {
            return of(this.ordenarNovedades([...novedadesCambios, ...novedadesBase]));
          }

          return this.invitacionesService.getInvitacionesPorPostulaciones(idsPostulacion).pipe(
            map((invitaciones) => this.buildPostulanteNovedades(novedadesBase, invitaciones, postulacionesAjustadas, logs))
          );
        })
      );
    } else if (rol === 'ayudante_postulante' || rol === 'colaborador' || rol === 'recomendador') {
      if (rol === 'ayudante_postulante') {
        novedadesObservable$ = combineLatest([
          baseNovedades$,
          this.invitacionesService.getInvitacionesPorRol('ayudante_postulante'),
          this.postulacionesService.getMisPostulaciones(),
          this.postulacionesLogService.logs$,
        ]).pipe(
          map(([novedadesBase, invitaciones, postulaciones, logs]) => {
            const postulacionesAjustadas = this.patrocinioService.aplicarEstadoPatrocinio(postulaciones);
            const novedadesInvitacion = this.buildInvitacionNovedadesRecibidas('ayudante_postulante', [], invitaciones);
            const novedadesCambios = this.buildCambioNovedadesProceso('ayudante_postulante', postulacionesAjustadas, logs);
            return this.ordenarNovedades([...novedadesInvitacion, ...novedadesCambios, ...novedadesBase]);
          })
        );
      } else {
        novedadesObservable$ = combineLatest([baseNovedades$, this.invitacionesService.getInvitacionesPorRol(rol as RolInvitacion)]).pipe(
          map(([novedadesBase, invitaciones]) => this.buildInvitacionNovedadesRecibidas(rol as RolInvitacion, novedadesBase, invitaciones))
        );
      }
    } else if (rol === 'representante_institucional') {
      novedadesObservable$ = combineLatest([baseNovedades$, this.postulacionesService.getMisPostulaciones()]).pipe(
        map(([novedadesBase, postulaciones]) =>
          this.buildPatrocinioNovedadesRepresentante(novedadesBase, this.patrocinioService.aplicarEstadoPatrocinio(postulaciones))
        )
      );
    } else {
      novedadesObservable$ = baseNovedades$;
    }

    return novedadesObservable$.pipe(
      map((items) => this.aplicarEstadoLectura(items)),
      tap((items) => {
        this.notificacionesState$.next(items);
        this.recalcularConteo(items);
      })
    );
  }

  getNovedadesInicioPorRol(rol: RolPlataforma): Observable<Novedad[]> {
    if (!roleHasNotifications(rol)) {
      return of([]);
    }

    const baseNovedades$ = this.getBaseNovedadesPorRol(rol);

    if (rol === 'postulante' || rol === 'ayudante_postulante') {
      return combineLatest([baseNovedades$, this.postulacionesService.getMisPostulaciones()]).pipe(
        map(([novedadesBase, postulaciones]) =>
          this.buildResumenInicioPostulante(rol, novedadesBase, this.patrocinioService.aplicarEstadoPatrocinio(postulaciones))
        )
      );
    }

    if (rol === 'representante_institucional') {
      return combineLatest([baseNovedades$, this.postulacionesService.getMisPostulaciones()]).pipe(
        map(([novedadesBase, postulaciones]) =>
          this.ordenarNovedadesInicio(
            this.buildPatrocinioNovedadesRepresentante(novedadesBase, this.patrocinioService.aplicarEstadoPatrocinio(postulaciones))
          ).slice(0, 4)
        )
      );
    }

    return this.getNovedadesPorRol(rol).pipe(map((items) => this.ordenarNovedadesInicio(items).slice(0, 4)));
  }

  getNovedadesInstitucionales(rol: RolPlataforma = 'postulante'): Observable<Novedad[]> {
    return this.getBaseNovedadesPorRol(rol);
  }

  private getBaseNovedadesPorRol(rol: RolPlataforma): Observable<Novedad[]> {
    if (!environment.useMockApi) {
      return this.http
        .get<NovedadApiDto[]>(this.baseUrl, { params: { role: rol } })
        .pipe(map((items) => items.map((dto) => this.fromApiDto(dto))));
    }

    const payload = NOVEDADES_MOCK.map((item) => this.toApiDto(item));
    const novedades = payload.map((dto) => this.fromApiDto(dto)).filter((item) => item.roles.includes(rol));
    return of(novedades);
  }

  private buildPostulanteNovedades(
    baseNovedades: Novedad[],
    invitaciones: InvitacionPostulacion[],
    postulaciones: Array<{
      idPostulacion: string;
      folio: string;
      concurso: string;
      rolesPermitidos: RolPlataforma[];
      formularioData?: { patrocinadorAsignado?: string; patrocinioSolicitado?: string };
      fechaInicio?: string;
      fechaTermino?: string;
      ultimaActualizacion?: string;
    }>,
    logs: PostulacionAccionLog[]
  ): Novedad[] {
    const novedadesInvitaciones = invitaciones.map((item) => this.novedadDesdeInvitacion(item));
    const novedadesPatrocinio = postulaciones
      .filter((item) => !!item.formularioData?.patrocinadorAsignado)
      .map((item) => this.novedadDesdePatrocinio(item));
    const novedadesCambios = this.buildCambioNovedadesProceso('postulante', postulaciones, logs);

    return this.ordenarNovedades([...novedadesInvitaciones, ...novedadesPatrocinio, ...novedadesCambios, ...baseNovedades]);
  }

  private buildInvitacionNovedadesRecibidas(
    rol: RolInvitacion,
    baseNovedades: Novedad[],
    invitaciones: InvitacionPostulacion[]
  ): Novedad[] {
    const novedadesInvitacion = invitaciones.map((item) => this.novedadRecibidaDesdeInvitacion(rol, item));
    return this.ordenarNovedades([...novedadesInvitacion, ...baseNovedades]);
  }

  private buildPatrocinioNovedadesRepresentante(baseNovedades: Novedad[], postulaciones: PostulacionResumen[]): Novedad[] {
    const novedadesPatrocinio = postulaciones
      .filter(
        (item) =>
          item.rolesPermitidos.includes('representante_institucional') &&
          item.solicitudPatrocinio &&
          this.requiereDecisionPatrocinio(item.estadoInvitacion)
      )
      .map((item) => this.novedadPendientePatrocinio(item));

    return this.ordenarNovedades([...novedadesPatrocinio, ...baseNovedades]);
  }

  private buildCambioNovedadesProceso(
    rol: Extract<RolPlataforma, 'postulante' | 'ayudante_postulante'>,
    postulaciones: Array<{
      idPostulacion: string;
      concurso: string;
      folio: string;
      rolesPermitidos: RolPlataforma[];
    }>,
    logs: PostulacionAccionLog[]
  ): Novedad[] {
    const idsRelacionados = new Set(
      postulaciones.filter((item) => item.rolesPermitidos.includes(rol)).map((item) => item.idPostulacion)
    );

    return logs
      .filter((item) => idsRelacionados.has(item.idPostulacion))
      .filter((item) => this.esAccionNotificableCambio(item.accion))
      .map((item) => this.novedadDesdeCambioProceso(rol, item));
  }

  private buildResumenInicioPostulante(
    rol: Extract<RolPlataforma, 'postulante' | 'ayudante_postulante'>,
    baseNovedades: Novedad[],
    postulaciones: Postulacion[]
  ): Novedad[] {
    const visibles = postulaciones.filter((item) => item.rolesPermitidos.includes(rol));
    const pendientes = visibles
      .filter((item) => item.estadoPostulacion === 'en_preparacion' || item.estadoPostulacion === 'observada')
      .map((item): Novedad => ({
        id: `inicio-pendiente-${rol}-${item.idPostulacion}`,
        titulo: `Postulacion pendiente: ${item.concurso}`,
        fecha: item.ultimaActualizacion ?? item.fechaInicio,
        tipo: item.estadoPostulacion === 'observada' ? 'alerta' : 'pendiente',
        detalle: `La postulacion ${item.idPostulacion} requiere revision antes del cierre del concurso.`,
        leida: false,
        accionLabel: 'Ver postulacion',
        accionRuta: `/postulaciones/formulario/${item.idPostulacion}`,
        accionQueryParams: { role: rol },
        roles: [rol],
      }));

    const cierres = visibles
      .filter((item) => item.estadoConcurso === 'abierto')
      .sort((a, b) => new Date(a.fechaTermino).getTime() - new Date(b.fechaTermino).getTime())
      .slice(0, 2)
      .map((item): Novedad => ({
        id: `inicio-cierre-${rol}-${item.idPostulacion}`,
        titulo: `Fecha de cierre: ${item.concurso}`,
        fecha: item.fechaTermino,
        tipo: 'alerta',
        detalle: `El concurso asociado a ${item.idPostulacion} cierra el ${item.fechaTermino}.`,
        leida: false,
        accionLabel: 'Ver postulacion',
        accionRuta: `/postulaciones/formulario/${item.idPostulacion}`,
        accionQueryParams: { role: rol },
        roles: [rol],
      }));

    const novedadesImportantes = this.deduplicarNovedades([...pendientes, ...cierres, ...baseNovedades]);
    return this.ordenarNovedadesInicio(novedadesImportantes).slice(0, 4);
  }

  private novedadDesdeInvitacion(item: InvitacionPostulacion): Novedad {
    const nombreDestino = `${item.nombresDestino ?? ''} ${item.apellidosDestino ?? ''}`.trim() || item.emailDestino || item.rolDestino;
    const etiquetaRol = item.rolDestino === 'recomendador' ? 'recomendador' : 'integrante del equipo';

    if (item.estadoRespuesta === 'aceptada') {
      return {
        id: `inv-${item.id}`,
        titulo: `${nombreDestino} acepto participar en la postulacion ${item.idPostulacion}`,
        fecha: item.fechaRespuesta ?? item.fechaSolicitud ?? new Date().toISOString(),
        tipo: 'completado',
        detalle: `El ${etiquetaRol} acepto la invitacion asociada al folio ${item.folio}. Ya puedes continuar el proceso con su participacion registrada.`,
        leida: true,
        roles: ['postulante'],
      };
    }

    if (item.estadoRespuesta === 'rechazada') {
      return {
        id: `inv-${item.id}`,
        titulo: `${nombreDestino} rechazo participar en la postulacion ${item.idPostulacion}`,
        fecha: item.fechaRespuesta ?? item.fechaSolicitud ?? new Date().toISOString(),
        tipo: 'alerta',
        detalle: `El ${etiquetaRol} rechazo la invitacion asociada al folio ${item.folio}. Si sigue siendo necesario, deberas solicitar participacion nuevamente.`,
        leida: false,
        roles: ['postulante'],
      };
    }

    return {
      id: `inv-${item.id}`,
      titulo: `Invitacion enviada a ${nombreDestino}`,
      fecha: item.fechaSolicitud ?? new Date().toISOString(),
      tipo: 'pendiente',
      detalle: `La invitacion para ${etiquetaRol} en la postulacion ${item.idPostulacion} fue enviada y esta pendiente de respuesta.`,
      leida: false,
      roles: ['postulante'],
    };
  }

  private novedadRecibidaDesdeInvitacion(rol: RolInvitacion, item: InvitacionPostulacion): Novedad {
    const rolAsignado = this.etiquetaRolInvitacion(item.rolDestino);
    const esPendiente = item.estadoRespuesta === 'pendiente';
    const base: Novedad = {
      id: `rec-${item.id}`,
      titulo: `Invitacion a ${item.concurso}`,
      fecha: item.fechaSolicitud ?? item.fechaRespuesta ?? new Date().toISOString(),
      tipo: esPendiente ? 'pendiente' : item.estadoRespuesta === 'aceptada' ? 'completado' : 'alerta',
      detalle: `Has sido invitado como ${rolAsignado} en la postulacion ${item.idPostulacion}. Revisa la invitacion para aceptar o rechazar tu participacion.`,
      leida: !esPendiente,
      roles: [rol],
    };

    if (!esPendiente) {
      return {
        ...base,
        detalle:
          item.estadoRespuesta === 'aceptada'
            ? `Tu participacion como ${rolAsignado} ya fue aceptada en la postulacion ${item.idPostulacion}.`
            : `La invitacion como ${rolAsignado} en la postulacion ${item.idPostulacion} fue rechazada.`,
      };
    }

    return {
      ...base,
      accionLabel: 'Ir a invitaciones',
      accionRuta: '/invitaciones',
      accionQueryParams: { role: rol },
    };
  }

  private novedadPendientePatrocinio(item: PostulacionResumen): Novedad {
    return {
      id: `pat-firma-${item.idPostulacion}`,
      titulo: `Firma de patrocinio pendiente para ${item.idPostulacion}`,
      fecha: item.ultimaActualizacion ?? item.fechaTermino ?? item.fechaInicio ?? new Date().toISOString(),
      tipo: 'pendiente',
      detalle: `La postulacion ${item.folio} del concurso ${item.concurso} requiere una decision institucional de patrocinio.`,
      leida: false,
      accionLabel: 'Ir a patrocinio',
      accionRuta: '/patrocinio',
      accionQueryParams: { role: 'representante_institucional' },
      roles: ['representante_institucional'],
    };
  }

  private novedadDesdeCambioProceso(
    rol: Extract<RolPlataforma, 'postulante' | 'ayudante_postulante'>,
    log: PostulacionAccionLog
  ): Novedad {
    if (log.accion === 'editar_formulario') {
      const actor = log.rol === 'ayudante_postulante' ? 'El ayudante' : 'La postulacion';
      return {
        id: `chg-edit-${log.timestamp}-${log.idPostulacion}`,
        titulo: `Se edito informacion en ${log.idPostulacion}`,
        fecha: log.timestamp,
        tipo: 'informacion',
        detalle: `${actor} actualizo campos del formulario para la postulacion ${log.folio}.`,
        leida: false,
        accionLabel: 'Ver postulacion',
        accionRuta: `/postulaciones/formulario/${log.idPostulacion}`,
        accionQueryParams: { role: rol },
        roles: [rol],
      };
    }

    if (log.accion === 'firmar_patrocinio') {
      return {
        id: `chg-sign-${log.timestamp}-${log.idPostulacion}`,
        titulo: `Patrocinio firmado para ${log.idPostulacion}`,
        fecha: log.timestamp,
        tipo: 'completado',
        detalle: `La firma de patrocinio institucional fue registrada para el folio ${log.folio}.`,
        leida: false,
        accionLabel: 'Ver postulacion',
        accionRuta: `/postulaciones/formulario/${log.idPostulacion}`,
        accionQueryParams: { role: rol },
        roles: [rol],
      };
    }

    const esRecomendador = log.accion === 'invitar_recomendador';
    return {
      id: `chg-team-${log.timestamp}-${log.idPostulacion}-${log.accion}`,
      titulo: esRecomendador ? `Se agrego un recomendador en ${log.idPostulacion}` : `Se agrego un integrante del equipo en ${log.idPostulacion}`,
      fecha: log.timestamp,
      tipo: 'completado',
      detalle: esRecomendador
        ? `Se registro una nueva invitacion de recomendacion para la postulacion ${log.folio}.`
        : `Se registro una nueva invitacion de colaboracion para la postulacion ${log.folio}.`,
      leida: false,
      accionLabel: 'Ver postulacion',
      accionRuta: `/postulaciones/formulario/${log.idPostulacion}`,
      accionQueryParams: { role: rol },
      roles: [rol],
    };
  }

  private novedadDesdePatrocinio(item: {
    idPostulacion: string;
    folio: string;
    formularioData?: { patrocinadorAsignado?: string; patrocinioSolicitado?: string };
    ultimaActualizacion?: string;
  }): Novedad {
    const nombrePatrocinador = item.formularioData?.patrocinadorAsignado ?? 'patrocinador pendiente';
    const estadoPatrocinio = item.formularioData?.patrocinioSolicitado ?? 'pendiente';

    return {
      id: `pat-${item.idPostulacion}`,
      titulo: `Patrocinio ${estadoPatrocinio} para la postulacion ${item.idPostulacion}`,
      fecha: item.ultimaActualizacion ?? new Date().toISOString(),
      tipo: estadoPatrocinio === 'confirmado' ? 'completado' : 'pendiente',
      detalle: `La solicitud de patrocinio asociada al folio ${item.folio} fue registrada para ${nombrePatrocinador}.`,
      leida: estadoPatrocinio === 'confirmado',
      roles: ['postulante'],
    };
  }

  private ordenarNovedades(novedades: Novedad[]): Novedad[] {
    return [...novedades].sort((a, b) => {
      const fechaA = a.fecha ? new Date(a.fecha).getTime() : 0;
      const fechaB = b.fecha ? new Date(b.fecha).getTime() : 0;
      return fechaB - fechaA;
    });
  }

  private ordenarNovedadesInicio(novedades: Novedad[]): Novedad[] {
    return [...novedades].sort((a, b) => {
      const prioridadA = this.prioridadNovedadInicio(a);
      const prioridadB = this.prioridadNovedadInicio(b);
      if (prioridadA !== prioridadB) return prioridadA - prioridadB;

      const fechaA = a.fecha ? new Date(a.fecha).getTime() : 0;
      const fechaB = b.fecha ? new Date(b.fecha).getTime() : 0;
      return fechaB - fechaA;
    });
  }

  private prioridadNovedadInicio(novedad: Novedad): number {
    const tipo = novedad.tipo ?? 'informacion';
    if (tipo === 'alerta') return 0;
    if (tipo === 'pendiente') return 1;
    if (!novedad.leida) return 2;
    if (tipo === 'informacion') return 3;
    return 4;
  }

  private deduplicarNovedades(novedades: Novedad[]): Novedad[] {
    const ids = new Set<string>();
    return novedades.filter((item) => {
      if (ids.has(item.id)) return false;
      ids.add(item.id);
      return true;
    });
  }

  private fromApiDto(dto: NovedadApiDto): Novedad {
    return {
      id: dto.id,
      titulo: dto.titulo,
      fecha: dto.fecha,
      tipo: dto.tipo,
      detalle: dto.detalle,
      leida: dto.leida ?? false,
      accionLabel: dto.accion_label,
      accionRuta: dto.accion_ruta,
      accionQueryParams: dto.accion_query_params,
      roles: dto.roles,
    };
  }

  private toApiDto(model: Novedad): NovedadApiDto {
    return {
      id: model.id,
      titulo: model.titulo,
      fecha: model.fecha,
      tipo: model.tipo,
      detalle: model.detalle,
      leida: model.leida,
      accion_label: model.accionLabel,
      accion_ruta: model.accionRuta,
      accion_query_params: model.accionQueryParams,
      roles: model.roles,
    };
  }

  private etiquetaRolInvitacion(rol: RolInvitacion): string {
    if (rol === 'ayudante_postulante') return 'ayudante de postulante';
    if (rol === 'recomendador') return 'recomendador';
    return 'integrante del equipo';
  }

  private requiereDecisionPatrocinio(estadoInvitacion: string): boolean {
    return estadoInvitacion !== 'aceptada' && estadoInvitacion !== 'rechazada' && estadoInvitacion !== 'observada';
  }

  private esAccionNotificableCambio(accion: PostulacionAccionLog['accion']): boolean {
    return accion === 'editar_formulario' || accion === 'invitar_colaborador' || accion === 'invitar_recomendador' || accion === 'firmar_patrocinio';
  }
}

type PostulacionResumen = {
  idPostulacion: string;
  concurso: string;
  folio: string;
  fechaInicio?: string;
  fechaTermino?: string;
  ultimaActualizacion?: string;
  estadoInvitacion: string;
  solicitudPatrocinio: boolean;
  rolesPermitidos: RolPlataforma[];
};
